import React, { useState } from 'react';
import { useGame } from '../contexts/GameContext';
import { abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { abilityStats, abilityText } from '../components/abilityTooltip';
import { isAbilityTrained } from '../../../shared/progression/abilityCurve';
import { abilityIconPath, hasAbilityIcon } from '../../rendering/abilities/AbilityIconRegistry';
import { ItemSlot } from '../components/ItemKit';
import { uiActionBridge } from '../framework/uiActionBridge';
import { TomeButton, TomeChapter, TomeFolio } from '../primitives/tome';
import { useKeyBindings } from '../hooks/useKeyBindings';
import { slotKeyLabel } from '../../systems/input/keyLabels';
import './AbilityBrowser.css';

/** Player spellbook. Authoring controls live in the existing operator projectile tools. */
export const AbilityBrowserContent: React.FC = () => {
  const { player } = useGame();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [slot, setSlot] = useState(1);
  const bindings = useKeyBindings();
  const learned = player?.learnedAbilities ?? [];
  const abilities = learned.flatMap(id => { const ability = abilityRegistry.getMetadata(id); return ability ? [ability] : []; });
  const describe = (ability: { id: string; description?: string }) => abilityText(ability.id, ability.description);
  const filtered = abilities.filter(ability => `${ability.name ?? ability.id} ${describe(ability)}`.toLowerCase().includes(search.toLowerCase()));
  const selected = selectedId && learned.includes(selectedId) ? abilityRegistry.getMetadata(selectedId) : null;
  // Plan 29 F2: the facts are the stat line computed from content.
  const facts = selected ? abilityStats(selected.id, player) : [];
  const cast = (id: string) => {
    if (!isAbilityTrained(learned, id)) return;
    ServiceRegistry.getInstance().get('EntitySystem')?.getPlayerManager()?.performAbility(id);
  };
  return <section className="spellbook" aria-label="Spellbook">
    <div className="spellbook-body">
      <TomeChapter className="spellbook-list-panel" title="Your Abilities" count={abilities.length}>
        <label className="spellbook-search">Find an ability<input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search abilities" /></label>
        <div className="spellbook-list">
          {filtered.map(ability => <div key={ability.id} className="spellbook-row" data-selected={selectedId === ability.id}>
            <ItemSlot item={{ id: ability.id, name: ability.name || ability.id,
              icon: hasAbilityIcon(ability.id) ? abilityIconPath(ability.id) : ability.icon }}
              size="md" ariaLabel={`${ability.name || ability.id} ability`} selected={selectedId === ability.id}
            onClick={() => setSelectedId(ability.id)} onDoubleClick={() => cast(ability.id)} draggable disableTooltip
            onDragStart={event => { event.dataTransfer?.setData('text/plain', `ability:${ability.id}`); if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove'; }} />
            <button type="button" onClick={() => setSelectedId(ability.id)} aria-pressed={selectedId === ability.id}>
              <strong>{ability.name || ability.id}</strong><span>{describe(ability) || 'Select to learn more.'}</span>
            </button>
          </div>)}
          {filtered.length === 0 && <p className="spellbook-empty">{abilities.length ? 'No abilities match your search.' : 'Visit a trainer to learn your first abilities.'}</p>}
        </div>
        <p className="spellbook-hint">Drag an ability to the hotbar, or select one to inspect it.</p>
      </TomeChapter>
      <div className="spellbook-detail">
        {selected ? <>
          <TomeFolio className="spellbook-folio" aria-label={selected.name || selected.id} shapeSeed={selected.id}>
          <span className="spellbook-kicker">Learned ability</span><h3>{selected.name || selected.id}</h3>
          <div className="spellbook-tags">{selected.tags?.filter(tag => !['basic','projectile'].includes(tag)).map(tag => <span key={tag}>{tag}</span>)}</div>
          <p>{describe(selected)}</p>
          <dl className="spellbook-facts">
            {facts.map(fact => <div key={fact.key} data-ability-stat={fact.key}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
          </dl>
          </TomeFolio>
          <div className="spellbook-action"><label className="spellbook-search">Hotbar slot<select value={slot} onChange={event => setSlot(Number(event.target.value))}>{Array.from({ length: 10 }, (_, index) => <option key={index + 1} value={index + 1}>Slot {index + 1} · key {slotKeyLabel(bindings, 1, index + 1) || 'unbound'}</option>)}</select></label><TomeButton tone="secondary" fullWidth onClick={() => { if (isAbilityTrained(learned, selected.id)) uiActionBridge.dispatch('updateActionBar', { slotIndex: slot, abilityId: selected.id }); }}>Assign to hotbar</TomeButton><TomeButton tone="primary" fullWidth onClick={() => cast(selected.id)}>Cast {selected.name || selected.id}</TomeButton></div>
        </> : <TomeFolio muted className="spellbook-folio" aria-label="No ability selected" shapeSeed="spellbook"><p className="spellbook-empty">Select an ability to view details</p></TomeFolio>}
      </div>
    </div>
  </section>;
};
